import { instanceEmployee } from "../../../../services/instances";
import type {
  AssignTagsToEmployee,
  CreateTag,
  EmployeeTagRange,
  GetTag,
  GetTagsByEmployee,
  ReplaceEmployeeTagRanges,
  Tag,
} from "../types/services";

const getTags = async ({ params, alert }: GetTag) => {
  const { showAlert, t } = alert;

  let tags: Tag[] = [];

  await instanceEmployee
    .get("tag", { params: params })
    .then((response) => {
      tags = response.data.data.map((tag: any) => ({
        value: tag?.tag_id.toString() || "",
        label: tag?.tag_name.toString() || "",
        sublabel: "",
      }));

      localStorage.setItem("all_tags", JSON.stringify(tags));
    })
    .catch(() => {
      showAlert("error", t("alerts.error_get_tags"));
    });

  return { tags };
};

const createTag = async ({ tag_name, alert }: CreateTag): Promise<Tag | null> => {
  const { showAlert, t } = alert;

  try {
    const response = await instanceEmployee.post("/tag/create", { tag_name });
    const tag = response.data.data;

    return {
      value: tag?.tag_id.toString() || "",
      label: tag?.tag_name.toString() || tag_name,
      sublabel: "",
    };
  } catch (error: any) {
    console.error(error.message);
    showAlert("error", t("assign_tag.alerts.create_error.message"));
    return null;
  }
};

const assignTagsToEmployee = async ({
  params,
  alert,
}: AssignTagsToEmployee): Promise<boolean> => {
  const { showAlert, closeAlert, t } = alert;
  const key = showAlert("default", t("assign_tag.alerts.assign_inprogress.message"));

  try {
    await instanceEmployee.post("/employee_r_tag/assign_employee_to_tag", {
      employee_ids: params.employeeIds.map(Number),
      tag_ids: params.tagIds.map(Number),
      start_date: params.startDate,
      end_date: params.endDate,
      overwrite: params.overwrite,
    });

    closeAlert?.(key);
    showAlert("success", t("assign_tag.alerts.assign_success.message"));
    return true;
  } catch (error: any) {
    console.error(error.message);
    closeAlert?.(key);
    showAlert("error", t("assign_tag.alerts.assign_error.message"));
    return false;
  }
};

const getTagsByEmployee = async ({
  employeeId,
  alert,
}: GetTagsByEmployee): Promise<{ ranges: EmployeeTagRange[] }> => {
  const { showAlert, t } = alert;

  try {
    const response = await instanceEmployee.get(
      `/employee_r_tag/by_employee/${employeeId}`,
    );

    return { ranges: response.data.data ?? [] };
  } catch (error: any) {
    console.error(error.message);
    showAlert("error", t("assign_tag.alerts.ranges_error.message"));
    return { ranges: [] };
  }
};

const replaceEmployeeTagRanges = async ({
  params,
  alert,
}: ReplaceEmployeeTagRanges): Promise<boolean> => {
  const { showAlert, closeAlert, t } = alert;
  const key = showAlert("default", t("assign_tag.alerts.assign_inprogress.message"));

  try {
    if (params.ranges.length === 0) {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      await instanceEmployee.post("/employee_r_tag/assign_employee_to_tag", {
        employee_ids: [Number(params.employeeId)],
        tag_ids: [],
        start_date: today.toISOString().split("T")[0],
        end_date: tomorrow.toISOString().split("T")[0],
        replace_all: true,
        overwrite: true,
      });
    }

    for (const [index, range] of params.ranges.entries()) {
      await instanceEmployee.post("/employee_r_tag/assign_employee_to_tag", {
        employee_ids: [Number(params.employeeId)],
        tag_ids: [Number(range.tagId)],
        start_date: range.startDate,
        end_date: range.endDate,
        replace_all: index === 0,
        overwrite: index === 0,
      });
    }

    closeAlert?.(key);
    showAlert("success", t("assign_tag.alerts.assign_success.message"));
    return true;
  } catch (error: any) {
    console.error(error.message);
    closeAlert?.(key);
    showAlert("error", t("assign_tag.alerts.assign_error.message"));
    return false;
  }
};

export {
  getTags,
  createTag,
  assignTagsToEmployee,
  getTagsByEmployee,
  replaceEmployeeTagRanges,
};
